import axios from "axios";
import { API_PATH } from "../../../constants/ApiPath";
import { uploadDirectToS3 } from "../../../util/S3Functions";

const PRESIGNED_ENDPOINT = `${API_PATH}/partner/post-visit-doc-upload/`;
const FINISH_ENDPOINT = `${API_PATH}/partner/post-visit-doc-upload-finish/`;
const SESSION_ENDPOINT = `${API_PATH}/partner/post-visit-doc-session/`;

export async function fetchPostVisitDocSession(token) {
  const res = await axios.get(SESSION_ENDPOINT, {
    params: { token },
  });
  return res.data;
}

export async function getPostVisitDocPresigned(token, files) {
  if (!files || !files.length) return [];
  const file_names = files.map((f) => f.name);
  const content_types = files.map((f) => f.type || "application/octet-stream");
  const res = await axios.post(PRESIGNED_ENDPOINT, {
    token,
    file_names,
    content_types,
  });
  if (res.data?.status === 200) {
    return res.data.presigned_post_data_list || [];
  }
  const err = new Error(res.data?.message || "Could not get upload link");
  err.response = res;
  throw err;
}

export async function postVisitDocUploadFinish(token, fileId, fileKey) {
  try {
    const res = await axios.post(FINISH_ENDPOINT, {
      token,
      file_id: fileId,
      file_key: fileKey,
    });
    if (res.data?.status === 200) {
      return { success: true, needRetry: false };
    }
    return { success: false, needRetry: false };
  } catch (err) {
    const status = err.response && err.response.status;
    if (status === 404) {
      return { success: false, needRetry: true };
    }
    throw err;
  }
}

export async function uploadPostVisitFilesWithRetry(
  token,
  fileList,
  maxRetries = 2,
  onProgress
) {
  const uploadedIds = [];
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      const presignedList = await getPostVisitDocPresigned(token, fileList);
      const ids = [];
      for (let i = 0; i < fileList.length; i += 1) {
        if (onProgress) {
          onProgress(i + 1, fileList.length, fileList[i].name);
        }
        const presigned = presignedList[i];
        if (!presigned) continue;
        await uploadDirectToS3(fileList[i], presigned);
        const fileKey = presigned.file_key || (presigned.fields && presigned.fields.key);
        const finishResult = await postVisitDocUploadFinish(token, presigned.file_id, fileKey);
        if (!finishResult.success && !finishResult.needRetry) {
          throw new Error("Finish upload failed");
        }
        if (finishResult.needRetry) {
          throw new Error("File not found in S3 (404)");
        }
        ids.push(presigned.file_id);
      }
      return { success: true, fileIds: ids };
    } catch (error) {
      if (attempt >= maxRetries) {
        throw error;
      }
    }
  }
  return { success: false, fileIds: uploadedIds };
}

export async function submitPostVisitDocBatch(token, fileIds) {
  const res = await axios.post(`${API_PATH}/partner/post-visit-doc-submit/`, {
    token,
    file_ids: fileIds,
  });
  return res.data;
}

export function isPostVisitSessionExpiredResponse(data) {
  return data && (data.status === 410 || data.reason === "ticket_closed" || data.reason === "session_closed");
}

export function truncateFilenameWithExt(name, maxLength = 32) {
  if (!name || name.length <= maxLength) return name;
  const dotIndex = name.lastIndexOf(".");
  const extension = dotIndex > 0 ? name.slice(dotIndex) : "";
  const baseName = dotIndex > 0 ? name.slice(0, dotIndex) : name;
  const ellipsis = "...";
  const baseBudget = maxLength - ellipsis.length - extension.length;
  if (baseBudget <= 0) {
    return name.slice(0, Math.max(maxLength - ellipsis.length, 0)) + ellipsis;
  }
  return baseName.slice(0, baseBudget) + ellipsis + extension;
}

export function filenameMaxLengthForViewport(width) {
  if (width < 360) return 18;
  if (width < 480) return 24;
  if (width < 768) return 32;
  return 44;
}
